import { useQuery } from "@apollo/client";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import moment from "moment";
import { ReactNode } from "react";
import {
  GetProfileOverviewQueryQuery,
  GetProfileOverviewQueryQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar, isAdminVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { lastSeenStr } from "@helpers/constants";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import getProfileOverviewQuery from "./graphql/get-profile-overview-query";
import ProfileActivities from "./overview-tab/components/profile-activities";
import ProfileArtifacts from "./overview-tab/components/profile-artifacts";
import ProfileCoachingInsights from "./overview-tab/components/profile-coaching-insights";
import ProfileLastOneonone from "./overview-tab/components/profile-last-oneonone";
import ProfileReviews from "./overview-tab/components/profile-reviews";
import ProfileContainer from "./profile-container";
import ProfileEmptyState from "./profile-empty-state";

const ProfilePageItem = ({
  label,
  children,
}: {
  label: string;
  children: string | ReactNode;
}) => {
  return (
    <div className="flex items-start gap-2 text-gray-600">
      <div className="font-medium text-gray-500 w-32 shrink-0">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ProfileOverview = ({ profileId }: { profileId: number }) => {
  const label = useLabel();

  const nextOneononeAfterDate = moment().startOf("hour").format();
  const lastOneononeBeforeDate = moment().startOf("hour").format();
  const { data, loading } = useQuery<
    GetProfileOverviewQueryQuery,
    GetProfileOverviewQueryQueryVariables
  >(getProfileOverviewQuery, {
    variables: {
      userId: profileId || -1,
      nextOneononeAfterDate,
      lastOneononeBeforeDate,
    },
    skip: !profileId,
  });
  const profile = data?.user;
  const roleStr =
    profile?.currentRoles.map((role) => role.title).join(", ") || "";
  const reports = profile?.directReports
    ? assertEdgesNonNull(profile.directReports)
    : [];

  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  return (
    <div>
      {loading ? (
        <Loading>Loading profile...</Loading>
      ) : !profile ? (
        <div>
          <ProfileEmptyState>Profile not found.</ProfileEmptyState>
        </div>
      ) : (
        <div className="flex flex-col-reverse xl:flex-row gap-8">
          <div className="flex-1 flex flex-col gap-6">
            {waffle.flag_is_active("coaching-insights") && (
              <ProfileCoachingInsights profile={profile} />
            )}
            <ProfileLastOneonone data={data} />
            <ProfileArtifacts profile={profile} />
            <ProfileActivities profileId={profile.id} />
          </div>
          <div className="xl:w-104 2xl:w-120 shrink-0 flex flex-col gap-6">
            <ProfileContainer>
              <div>
                <div className="flex items-center gap-4">
                  <Avatar
                    user={profile}
                    size={14}
                    className="h-[56px] w-[56px] rounded-md"
                  />
                  <div>
                    <div className="font-medium text-gray-900 text-lg">
                      {profile.name}
                    </div>
                    {roleStr && (
                      <div className="text-gray-500 text-sm tracking-tight">
                        {roleStr}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-4 pt-6 pb-6">
                  <ProfilePageItem label="Timezone">
                    {profile.timezone}
                  </ProfilePageItem>
                  <ProfilePageItem label="Email">
                    {profile.email}
                  </ProfilePageItem>
                  {profile.lastSeenStr && (
                    <ProfilePageItem label="Last seen">
                      {lastSeenStr[profile.lastSeenStr]}
                    </ProfilePageItem>
                  )}
                </div>
                <div className="flex flex-col gap-4 border-t py-6">
                  {roleStr && (
                    <ProfilePageItem label="Role">{roleStr}</ProfilePageItem>
                  )}
                  <ProfilePageItem
                    label={label("team", {
                      pluralize: true,
                      titleCase: true,
                    })}
                  >
                    {profile.teamsList.length === 0
                      ? `No ${label("team", { pluralize: true })}.`
                      : profile.teamsList.map((team) => team.title).join(", ")}
                  </ProfilePageItem>
                  <ProfilePageItem label="Managers">
                    {profile.managersList.length === 0
                      ? "No managers."
                      : profile.managersList.map((manager, i) => (
                          <span key={manager.id}>
                            {i > 0 && ", "}
                            <AppLink
                              to={getUrlUsingTypename(manager)}
                              className="hover:underline"
                            >
                              {manager.name}
                            </AppLink>
                          </span>
                        ))}
                  </ProfilePageItem>
                  <ProfilePageItem label="Reports">
                    {reports.length === 0
                      ? "No direct reports."
                      : reports.map((report, i) => (
                          <span key={report.id}>
                            {i > 0 && ", "}
                            <AppLink
                              to={getUrlUsingTypename(report)}
                              className="hover:underline"
                            >
                              {report.name}
                            </AppLink>
                          </span>
                        ))}
                  </ProfilePageItem>
                </div>
              </div>
              <div>
                <Button to={`/org-chart?userId=${profile.id}`}>
                  View in org chart
                  <ExternalLinkIcon className="text-gray-400 h-4 w-4" />
                </Button>
              </div>
            </ProfileContainer>

            {(isAdmin || isManager || profile.id === currentUser.id) && (
              <ProfileReviews profileId={profile.id} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOverview;
